import { FC, memo, MouseEventHandler, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UseReactToPrintFn } from "react-to-print";
import { Box, Button, Paper, Stack, Typography, useTheme } from "@mui/material";
import { IProjectConfig } from "types";
import { APP_COMPANY_NAME } from "consts";
import { DateTime } from "components/DateTime";
import { InputField } from "components/InputField";
import getStyles from "./styles";
import DownloadLinks from "./DownloadLinks";

type Props = {
  projectConfig: IProjectConfig;
  isPrintMode: boolean;
  showButtons: boolean;
  pdfUrl: string;
  ldtUrl: string;
  recadzUrl: string;
  requestHash: string;
  updateProject: (key: string, value: string) => void;
  reactToPrint: UseReactToPrintFn;
  carbonePrint?: () => void;
};

const LightingCalculation: FC<Props> = ({
  projectConfig,
  pdfUrl,
  ldtUrl,
  recadzUrl,
  requestHash,
  showButtons,
  isPrintMode,
  reactToPrint,
  carbonePrint,
  updateProject,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).LightingCalculation, [theme]);
  const language = i18n.resolvedLanguage;

  const { project_id: projectId, project_name: projectName, location: projectLocation, name_of_road: roadName, comment } = projectConfig;

  const handleClipboard: MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    try {
      const clipText = await navigator.clipboard.readText();
      const formData = JSON.parse(clipText);

      [
        ["project_id", formData.projectId],
        ["project_name", formData.projectName],
        ["location", formData.location],
        ["name_of_road", formData.streetName],
      ].forEach(([key, value]) => {
        if (value) {
          updateProject(key, value);
        }
      });
    } catch (err) {
      console.error("Failed to read clipboard contents: ", err);
    }
  };

  return (
    <Box component="section" sx={styles.container}>
      <Typography variant="h2" sx={styles.title}>
        {t("Lighting Calculation")}
      </Typography>

      <Paper sx={styles.card}>
        <Stack component="fieldset" sx={styles.form}>
          <Stack component="legend" sx={styles.header}>
            <DateTime />
            {APP_COMPANY_NAME.toLowerCase() === "signify" && language === "de" && (
              <Button size="small" variant="contained" onClick={handleClipboard} sx={styles.paste}>
                {t("Paste from Clipboard")}
              </Button>
            )}
          </Stack>

          <Box sx={styles.grid}>
            <InputField
              id="report-id"
              name="report-id"
              value={projectId}
              label={t("Project ID")}
              placeholder={t("Example: ID 4444")}
              maxLength={60}
              onChange={(_, value) => updateProject("project_id", value as string)}
              sx={!projectId ? { displayPrint: "none" } : {}}
            />

            <InputField
              id="report-name"
              name="report-name"
              value={projectName}
              label={t("Project Name")}
              placeholder={t("Project Name")}
              onChange={(_, value) => updateProject("project_name", value as string)}
              sx={!projectName ? { displayPrint: "none" } : {}}
            />

            <InputField
              id="report-location"
              name="report-location"
              value={projectLocation}
              label={t("Project Location")}
              placeholder={t("Example: City, Country")}
              maxLength={60}
              onChange={(_, value) => updateProject("location", value as string)}
              sx={!projectLocation ? { displayPrint: "none" } : {}}
            />

            <InputField
              id="report-name_of_road"
              name="report-name_of_road"
              value={roadName}
              label={t("Name of road")}
              placeholder={t("Example: Main Street")}
              maxLength={60}
              onChange={(_, value) => updateProject("name_of_road", value as string)}
              sx={!roadName ? { displayPrint: "none" } : {}}
            />

            <InputField
              id="report-comment"
              name="report-comment"
              value={comment}
              label={t("Comment")}
              placeholder={t("Please insert your comment here")}
              maxLength={isPrintMode ? 200 : 500}
              rows={5}
              multiline
              onChange={(_, value) => updateProject("comment", value as string)}
              onPaste={(e) => {
                e.preventDefault();
                return false;
              }}
              sx={{ resize: "none", ...(!comment ? { displayPrint: "none" } : {}) }}
            />
          </Box>

          {showButtons && (
            <DownloadLinks
              id="downloads_top"
              pdfUrl={pdfUrl}
              ldtUrl={ldtUrl}
              recadzUrl={recadzUrl}
              requestHash={requestHash}
              updateProject={updateProject}
              reactToPrint={reactToPrint}
              carbonePrint={carbonePrint}
              sx={styles.buttons}
            />
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default memo(
  LightingCalculation,
  (prevProps, nextProps) =>
    prevProps.projectConfig.project_id === nextProps.projectConfig.project_id &&
    prevProps.projectConfig.project_name === nextProps.projectConfig.project_name &&
    prevProps.projectConfig.location === nextProps.projectConfig.location &&
    prevProps.projectConfig.name_of_road === nextProps.projectConfig.name_of_road &&
    prevProps.projectConfig.comment === nextProps.projectConfig.comment &&
    prevProps.isPrintMode === nextProps.isPrintMode &&
    prevProps.showButtons === nextProps.showButtons &&
    prevProps.pdfUrl === nextProps.pdfUrl &&
    prevProps.ldtUrl === nextProps.ldtUrl &&
    prevProps.recadzUrl === nextProps.recadzUrl &&
    prevProps.requestHash === nextProps.requestHash &&
    prevProps.reactToPrint === nextProps.reactToPrint &&
    prevProps.updateProject === nextProps.updateProject
);
